import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { db } from "@/lib/db-client";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/format";
import { Printer, ArrowLeft } from "lucide-react";
import logo from "@/assets/logo.png";

export const Route = createFileRoute("/ticket/$id")({
  component: TicketPage,
});

function TicketPage() {
  const { id } = Route.useParams();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [ticket, clients, materials, ticketMats, settings] = await Promise.all([
        db.from("weigh_tickets").select("*").eq("id", id).single(),
        db.from("clients").select("*"),
        db.from("materials").select("*"),
        db.from("ticket_materials").select("*").eq("ticket_id", id),
        db.from("settings").select("company_name").eq("id", 1).single(),
      ]);
      
      const clientsMap = new Map((clients.data || []).map(c => [c.id, c]));
      const materialsMap = new Map((materials.data || []).map(m => [m.id, m]));
      
      const enriched = ticket.data ? {
        ...ticket.data,
        clients: clientsMap.get(ticket.data.client_id),
        ticket_materials: (ticketMats.data || []).map(tm => ({
          ...tm,
          materials: materialsMap.get(tm.material_id),
        })),
      } : null;
      
      setData({ ticket: enriched, settings: settings.data });
      setLoading(false);
      // Auto-print
      setTimeout(() => window.print(), 400);
    })();
  }, [id]);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Chargement…</div>;
  if (!data?.ticket) return <div className="p-8 text-center">Ticket introuvable</div>;

  const t = data.ticket;
  const company = data.settings?.company_name || "Mon entreprise";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card no-print">
        <div className="mx-auto max-w-2xl px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Nouvelle pesée
          </Link>
          <Button onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Imprimer</Button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl p-4">
        <div className="print-ticket bg-white rounded-md border p-6 mx-auto" style={{ maxWidth: 360 }}>
          <div className="text-center">
            <img src={logo} alt="LocMat" className="mx-auto h-12 w-auto mb-2" />
            <div className="font-bold text-base">{company}</div>
            <div className="text-xs">BON DE PESÉE</div>
            <div className="text-xs">{t.type === "depot" ? "DÉPÔT" : "REPRISE"}</div>
          </div>
          <hr className="my-2 border-dashed" />
          <div className="text-xs space-y-0.5">
            <div>Date : {fmtDateTime(t.occurred_at)}</div>
            <div>Chantier : {t.site_name}</div>
            <div>Client : {t.clients?.name}</div>
            <div>Camion : {t.truck_plate}</div>
          </div>
          <hr className="my-2 border-dashed" />
          <div className="text-xs">
            <div className="font-bold mb-1">MATIÈRES</div>
            {t.ticket_materials?.map((m: any, i: number) => (
              <div key={i} className="flex justify-between">
                <span>{m.materials?.name}</span>
                <span>{Number(m.tonnage).toFixed(3)} t</span>
              </div>
            ))}
            <div className="flex justify-between font-bold border-t border-dashed mt-1 pt-1">
              <span>TOTAL</span>
              <span>
                {t.ticket_materials?.reduce((s: number, m: any) => s + Number(m.tonnage), 0).toFixed(3)} t
              </span>
            </div>
          </div>
          <hr className="my-2 border-dashed" />
          <div className="text-xs">
            <div>Signataire : {t.driver_signature_name}</div>
            {t.signature_data_url && (
              <img src={t.signature_data_url} alt="Signature" style={{ maxHeight: 60, maxWidth: "100%" }} />
            )}
          </div>
          <div className="text-center text-[10px] mt-3">N° {t.id.slice(0, 8).toUpperCase()}</div>
        </div>
      </main>
    </div>
  );
}
